import React, { useEffect, useMemo, useState } from "react";
import {
  Paper,
  Grid,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Stack,
  Chip,
  TableContainer,
  Pagination,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import axios from "axios";

import VisualizationDialog from "./VisualizationDialog";
import visualizationApi from "../../hooks/GET/visualization";
import { endpoint } from "../../config";
import { customToast } from "../../lib/toastLib";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import ListDataViewDialog from "../../components/ListDataViewDialog";

function Visualization() {
  // visualization list logic start
  const [visualizationList, setVisualizationList] = useState([]);
  const [refetchVisualizationList, setRefetchVisualizationList] = useState(0);
  const [totalResult, setTotalResult] = useState(0);
  const isSmall = useMediaQuery('(max-width:536px)');
  const isSmall2 = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    visualizationApi()
      .then((data) => {
        setVisualizationList(data);


      })
      .catch((error) => console.log(error));
  }, [refetchVisualizationList]);

  const handleDelete = (id) => {
    const fieldValues = [...visualizationList];
    // const index = page * rowsPerPage + i;
    axios
      .delete(`${endpoint}/api/visualization/${id}`)
      .then(() => {
        fieldValues.splice(fieldValues.findIndex(obj => obj.id === id), 1);
        setVisualizationList(fieldValues);


        customToast.success("Visualization deleted successfully");
      })
      .catch((error) => {
        if (error.response) {
          customToast.error(error.response.data?.message);
        } else {
          customToast.error(error.message);
        }
      });
  };
  // visualization list logic end

  //table pagination logic start
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOption = [5, 10, 20];

  const handleChangePage = (e, newPage) => {
    setPage(newPage - 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = rowsPerPage -
    Math.min(rowsPerPage, totalResult - page * rowsPerPage);
  // rowsPerPage -
  // Math.min(rowsPerPage, visualizationList.length - page * rowsPerPage);
  //table pagination logic end

  // visualization dialog logic start
  const [visualizationDialogState, setVisualizationDialogState] = useState({
    open: false,
    id: 0,
    name: "",
    description: "",
    embedCode: "",
    categories: [],
    groupId: "",
    reportId: "",
    isProtected: false,
  });

  const handleVisualizationDialogOpen = (
    id,
    name,
    description,
    embedCode,
    categories,
    groupId,
    reportId,
    isProtected
  ) => {
    setVisualizationDialogState({
      open: true,
      id: id,
      name: name,
      description: description,
      embedCode: embedCode,
      categories: categories,
      groupId: groupId,
      reportId: reportId,
      isProtected: isProtected,
    });
  };

  const handleVisualizationDialogClose = (doRefetch) => {
    if (doRefetch) {
      setRefetchVisualizationList(refetchVisualizationList + 1);
    }
    setVisualizationDialogState({
      open: false,
      id: 0,
      name: "",
      description: "",
      embedCode: "",
      categories: [],
      groupId: "",
      reportId: "",
      isProtected: false,
    });
  };
  // visualization dialog logic end

  // delete confirmation dialog logic start
  const [deleteDialogState, setDeleteDialogState] = useState({
    open: false,
    title: "",
    message: "",
    id: "",
  });

  const handleDeleteDialogOpen = (title, message, id) => {
    setDeleteDialogState({
      open: true,
      title: title,
      message: message,
      id: id,
    });
  };

  const handleDeleteDialogClose = (isConfirmed, id) => {
    // if confirmed than delete
    if (isConfirmed) {
      handleDelete(id);
    }
    setDeleteDialogState({
      open: false,
      title: "",
      message: "",
      id: "",
    });
  };
  // delete confirmation dialog logic end

  // search logic start
  const [search, setSearch] = useState("");

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    setPage(0);
  };

  const getFilteredVisualizationList = useMemo(() => {
    let filterdata = visualizationList;
    filterdata = filterdata.filter((obj) =>
      JSON.stringify(obj).match(new RegExp(search, "gi"))
    );
    setTotalResult(filterdata.length);
    if (filterdata.length !== 0) {
      const lastPage = Math.ceil(filterdata.length / rowsPerPage);
      if (lastPage === page)
        setPage(lastPage - 1);
    }
    return filterdata;
  }, [visualizationList, search, page, rowsPerPage]);
  // search logic end

  const [visualizationCatgList, setvisualizationCatgList] = useState({
    open: false,
    title: "",
    data: []
  });
  const handleViewAllDialogOpen = (data) => {
    setvisualizationCatgList({
      open: true,
      title: "Visualization Categories",
      data: data
    });
  };

  const handleViewAllDialogClose = () => {
    setvisualizationCatgList({
      open: false,
      title: "",
      data: []
    });
  };

  return (
    <Paper variant="outlined" sx={{ padding: 2 }}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Grid item>
          <Typography sx={{ display: "contents" }}>Show</Typography>
          <TextField
            select
            name="rowsPerPage"
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            size="small"
            sx={{ verticalAlign: "middle", margin: 1, minWidth: 75 }}
          >
            {rowsPerPageOption.map((option, idx) => (
              <MenuItem key={idx} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <Typography sx={{ display: "contents" }}>entries</Typography>
          <TextField
            name="search"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search..."
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ verticalAlign: "middle", marginLeft: isSmall ? 0 : 1, marginTop: 1, marginRight: isSmall ? 2 : 1, marginBottom: 1 }}
          />
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() =>
              handleVisualizationDialogOpen(null, "", "", "", [], "", "", false)
            }
          >
            Create
          </Button>
        </Grid>
      </Grid>

      {/* Table part */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">Sr. No.</TableCell>
              <TableCell align="center">Name</TableCell>
              <TableCell align="center">Description</TableCell>
              {/* <TableCell align="center">Embed Code</TableCell> */}
              {/* <TableCell align="center">Group Id</TableCell> */}
              <TableCell align="center">Report Id</TableCell>
              {/* <TableCell align="center">Protected</TableCell> */}
              <TableCell align="center" width="30%">Categories</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rowsPerPage > 0
              ? getFilteredVisualizationList.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              )
              : getFilteredVisualizationList
            ).map((fieldValue, idx) => (
              <TableRow key={idx} hover>
                <TableCell align="center">{page * rowsPerPage + idx + 1}</TableCell>
                <TableCell align="center">{fieldValue.name}</TableCell>
                <TableCell align="center">{fieldValue.description}</TableCell>
                {/* <TableCell align="center">
                  {fieldValue.embedCode.length > 50
                    ? `${fieldValue.embedCode.substring(0, 50)}...`
                    : fieldValue.embedCode}
                </TableCell> */}
                {/* <TableCell align="center">{fieldValue.groupId}</TableCell> */}
                <TableCell align="center">{fieldValue.reportId}</TableCell>
                {/* <TableCell align="center">
                  {fieldValue.isProtected ? "Yes" : "No"}
                </TableCell> */}
                <TableCell align="center" width="30%">
                  <Stack direction="row" spacing={1} justifyContent="center" flexWrap={'wrap'} sx={{ rowGap: 1 }}>
                    {fieldValue.categories.slice(0, 3).map((cv, cvi) => (
                      <Chip label={cv.name} key={cvi} size="small" />
                    ))}
                    {fieldValue.categories.length > 3
                      ? <Button size="small" sx={{ textTransform: 'capitalize', fontSize: 10 }} onClick={() => handleViewAllDialogOpen(fieldValue.categories)}>View All</Button>
                      : null}
                  </Stack>
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" justifyContent="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() =>
                          handleVisualizationDialogOpen(
                            fieldValue.id,
                            fieldValue.name,
                            fieldValue.description,
                            fieldValue.embedCode,
                            fieldValue.categories,
                            fieldValue.groupId,
                            fieldValue.reportId,
                            fieldValue.isProtected
                          )
                        }
                      >
                        <EditOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() =>
                          handleDeleteDialogOpen(
                            `Delete visualization '${fieldValue.name}'`,
                            "Are you sure you want to delete this visualization?",
                            fieldValue.id
                          )
                        }
                      >
                        <DeleteOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell align="center" colSpan={9}>
                  {totalResult === 0 &&
                    "No visualizations available to display"}
                  {/* {visualizationList.length === 0 &&
                    "No visualizations available to display"} */}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <VisualizationDialog
        open={visualizationDialogState.open}
        handleClose={handleVisualizationDialogClose}
        dataId={visualizationDialogState.id}
        dataName={visualizationDialogState.name}
        dataDescription={visualizationDialogState.description}
        dataEmbedCode={visualizationDialogState.embedCode}
        dataCategories={visualizationDialogState.categories}
        dataGroupId={visualizationDialogState.groupId}
        dataReportId={visualizationDialogState.reportId}
        dataIsProtected={visualizationDialogState.isProtected}
      />

      <ConfirmationDialog
        open={deleteDialogState.open}
        handleClose={handleDeleteDialogClose}
        title={deleteDialogState.title}
        message={deleteDialogState.message}
        data={deleteDialogState.id}
      />

      <ListDataViewDialog
        open={visualizationCatgList.open}
        title={visualizationCatgList.title}
        data={visualizationCatgList.data}
        handleClose={handleViewAllDialogClose}
      />

      {/* {visualizationList.length > 0 && ( */}
      {totalResult > 0 && (
        <Grid container justifyContent="center" sx={{ mt: 2 }} spacing={1}>
          <Grid item>
            <Button
              size="small"
              onClick={(e) => handleChangePage(e, page)}
              disabled={page === 0}
            >
              Previous
            </Button>
          </Grid>
          <Grid item>
            <Pagination
              // count={Math.ceil(visualizationList.length / rowsPerPage)}
              count={Math.ceil(totalResult / rowsPerPage)}
              page={page + 1}
              onChange={handleChangePage}
              variant="outlined"
              shape="rounded"
              color="secondary"
              hidePrevButton
              hideNextButton
              size={isSmall2 ? 'small' : 'medium'}
            />
          </Grid>
          <Grid item>
            <Button
              size="small"
              onClick={(e) => handleChangePage(e, page + 2)}
              // disabled={
              //   Math.ceil(visualizationList.length / rowsPerPage) === page + 1
              // }
              disabled={
                Math.ceil(totalResult / rowsPerPage) === page + 1
              }
            >
              Next
            </Button>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
}

export default Visualization;
